import { useState } from 'react';
import { X, Check, Crown, Star, Zap, Users, FileText, Bell, MapPin, Shield, Clock, Tag, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useFounderSpots, withFounderStatus } from '../hooks/useFounderSpots';
import { PlanCards } from './PlanCards';
import { BILLING_OPTIONS, plansForPeriod } from '../lib/pricingPlans';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PlanSelectionModal
 * 
 * Modal per la scelta del piano di upgrade.
 * Mostra i piani disponibili con features e prezzi.
 * Dopo la scelta, procede al checkout Stripe.
 */
const PlanSelectionModal = ({ 
  isOpen, 
  onClose, 
  currentPlan = 'trial',
  userId,
  famigliaId,
  featureRequested = null, // es: "report", "ricetta", "scudo", "gps"
  blocking = false, // prova scaduta: schermata intera senza chiusura
  onLogout = null
}) => {
  const [loading, setLoading] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const founder = useFounderSpots(isOpen);

  if (!isOpen) return null;

  const billingOptions = BILLING_OPTIONS;
  const visiblePlans = plansForPeriod(billingPeriod).map((plan) => withFounderStatus(plan, founder));

  const handleSelectPlan = async (plan) => {
    const planId = plan.id;
    setLoading(planId);
    
    try {
      const payload = {
        plan: planId,
        billing_period: billingPeriod,
        origin_url: window.location.origin
      };
      
      // Aggiungi solo se presenti
      if (userId) payload.user_id = userId;
      if (famigliaId) payload.famiglia_id = famigliaId;
      
      console.log('Checkout payload:', payload);
      
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('Checkout response:', data);
      if (response.status === 409 && planId === 'fondatori') founder.markSoldOut();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        // Gestisci errore - detail può essere stringa o oggetto
        const errorMsg = typeof data.detail === 'string' 
          ? data.detail 
          : (data.detail?.message || data.detail?.msg || data.error || JSON.stringify(data.detail) || 'Errore durante la creazione del checkout');
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = typeof error === 'string' 
        ? error 
        : (error?.message || 'Errore durante il checkout');
      toast.error(errorMessage);
    } finally {
      setLoading(null);
    }
  };

  // Messaggio contestuale basato sulla feature richiesta
  const getContextMessage = () => {
    if (blocking) return "Effettua l'upgrade per continuare a usare CuraCheck";
    switch (featureRequested) {
      case 'report':
        return 'Per scaricare i report PDF, scegli un piano a pagamento';
      case 'ricetta':
        return 'La gestione ricette medico è disponibile con il piano Premium';
      case 'scudo':
        return 'Il Tasto Scudo Emergenza è disponibile dal piano Standard';
      case 'destinatari':
        return 'Per aggiungere più destinatari notifiche, effettua l\'upgrade';
      case 'gps':
        return 'Il registro delle passeggiate è disponibile con il piano Premium';
      default:
        return 'Scegli il piano più adatto alle tue esigenze';
    }
  };

  return (
    <div
      className={blocking ? 'fixed inset-0 bg-[#FAF9F6] z-[10000] overflow-y-auto' : 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto'}
      data-testid={blocking ? 'trial-expired-wall' : 'plan-selection-modal'}
    >
      <div className={blocking ? 'bg-white max-w-6xl w-full mx-auto min-h-full' : 'bg-white rounded-3xl max-w-6xl w-full shadow-2xl my-4 max-h-[95vh] overflow-y-auto'}>
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 p-6 flex items-center justify-between z-10">
          <div>
            {blocking && <p className="text-sm font-semibold text-[#EF4444] mb-1" data-testid="trial-expired-label">Prova scaduta</p>}
            <h2 className="text-2xl font-bold text-[#2C3E50]">Scegli il tuo piano</h2>
            <p className="text-[#64748B] mt-1">{getContextMessage()}</p>
          </div>
          {blocking ? (
            onLogout && (
              <Button onClick={onLogout} variant="outline" className="border-[#E5E7EB]" data-testid="trial-expired-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            )
          ) : (
            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              data-testid="plan-modal-close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Toggle Mensile/Trimestrale/Annuale */}
        <div className="flex justify-center py-4 bg-gray-50">
          <div className="bg-white rounded-full p-1 shadow-sm border border-gray-200 flex" data-testid="plan-modal-billing-toggle">
            {billingOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setBillingPeriod(option.id)}
                className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingPeriod === option.id
                    ? 'bg-[#4A7C59] text-white shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`plan-modal-billing-${option.id}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Plans Grid */}
        <div className="p-6 bg-[#F8FAFC]">
          <PlanCards
            plans={visiblePlans}
            period={billingPeriod}
            onSelect={handleSelectPlan}
            loadingPlanId={loading}
            currentPlanId={currentPlan}
          />
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4 bg-gray-50 rounded-b-3xl">
          <div className="flex items-center justify-center gap-6 text-sm text-[#64748B]">
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500" />
              Cancella quando vuoi
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500" />
              Pagamento sicuro
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500" />
              Nessun vincolo
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionModal;
